// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import Translation from '@app/features/messaging/state/Translation';
import ReverseImageSearch from '@app/features/search/state/ReverseImageSearch';
import SearchEngine from '@app/features/search/state/SearchEngine';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {SwitchGroup, SwitchGroupItem} from '@app/features/ui/components/SwitchGroup';
import {
	AddCustomSearchEngineModal,
	type SearchEngineMode,
} from '@app/features/user/components/modals/tabs/chat_settings_tab/AddCustomSearchEngineModal';
import styles from '@app/features/user/components/modals/tabs/chat_settings_tab/SearchEnginesTab.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {TrashIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface EngineLike {
	id: string;
	name: string;
	urlTemplate: string;
	enabled: boolean;
	isBuiltIn: boolean;
}
interface EngineSource {
	engines: Array<EngineLike>;
	enabledEngines: ReadonlyArray<EngineLike>;
	defaultEngine: EngineLike | null;
	setEnabled: (engineId: string, enabled: boolean) => void;
	setDefaultEngine: (engineId: string) => void;
	removeCustomEngine: (engineId: string) => void;
}
interface EngineSectionProps {
	mode: SearchEngineMode;
	store: EngineSource;
	defaultTitle: string;
	defaultDescription: string;
	builtInTitle: string;
	builtInDescription: string;
	customTitle: string;
	customDescription: React.ReactNode;
	addButtonLabel: string;
	noEnginesText: string;
	confirmTitle: string;
}
const EngineSection: React.FC<EngineSectionProps> = observer(
	({
		mode,
		store,
		defaultTitle,
		defaultDescription,
		builtInTitle,
		builtInDescription,
		customTitle,
		customDescription,
		addButtonLabel,
		noEnginesText,
		confirmTitle,
	}) => {
		const {t} = useLingui();
		const enabledEngines = store.enabledEngines;
		const builtInEngines = store.engines.filter((engine) => engine.isBuiltIn);
		const customEngines = store.engines.filter((engine) => !engine.isBuiltIn);
		const defaultEngineOptions: ReadonlyArray<SelectOption> = enabledEngines.map((engine) => ({
			value: engine.id,
			label: engine.name,
		}));
		const handleAddCustomEngine = useCallback(() => {
			ModalCommands.push(modal(() => <AddCustomSearchEngineModal mode={mode} />));
		}, [mode]);
		const handleEditCustomEngine = useCallback(
			(engineId: string, currentName: string, currentUrlTemplate: string) => {
				ModalCommands.push(
					modal(() => (
						<AddCustomSearchEngineModal
							mode={mode}
							editingEngineId={engineId}
							initialName={currentName}
							initialUrlTemplate={currentUrlTemplate}
						/>
					)),
				);
			},
			[mode],
		);
		const handleRemoveCustomEngine = useCallback(
			(engineId: string, engineName: string) => {
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={confirmTitle}
							description={
								<Trans>
									Are you sure you want to remove <strong>{engineName}</strong>?
								</Trans>
							}
							primaryText={t`Remove`}
							primaryVariant="danger-primary"
							onPrimary={() => {
								store.removeCustomEngine(engineId);
							}}
						/>
					)),
				);
			},
			[confirmTitle, store, t],
		);
		return (
			<>
				<SettingsTabSection title={defaultTitle} description={defaultDescription}>
					{enabledEngines.length > 0 ? (
						<Select
							value={store.defaultEngine?.id ?? ''}
							options={defaultEngineOptions}
							onChange={(value) => store.setDefaultEngine(value)}
						/>
					) : (
						<p className={styles.noEnginesText}>{noEnginesText}</p>
					)}
				</SettingsTabSection>
				<SettingsTabSection title={builtInTitle} description={builtInDescription}>
					<SwitchGroup>
						{builtInEngines.map((engine) => (
							<SwitchGroupItem
								key={engine.id}
								label={engine.name}
								value={engine.enabled}
								onChange={(value) => store.setEnabled(engine.id, value)}
							/>
						))}
					</SwitchGroup>
				</SettingsTabSection>
				<SettingsTabSection title={customTitle} description={customDescription}>
					{customEngines.length > 0 && (
						<SwitchGroup>
							{customEngines.map((engine) => (
								<div key={engine.id} className={styles.customEngineRow}>
									<SwitchGroupItem
										label={engine.name}
										value={engine.enabled}
										onChange={(value) => store.setEnabled(engine.id, value)}
									/>
									<div className={styles.customEngineActions}>
										<button
											type="button"
											className={styles.editButton}
											onClick={() => handleEditCustomEngine(engine.id, engine.name, engine.urlTemplate)}
											aria-label={t`Edit ${engine.name}`}
										>
											{t`Edit`}
										</button>
										<button
											type="button"
											className={styles.removeButton}
											onClick={() => handleRemoveCustomEngine(engine.id, engine.name)}
											aria-label={t`Remove ${engine.name}`}
										>
											<TrashIcon size={16} weight="fill" />
										</button>
									</div>
								</div>
							))}
						</SwitchGroup>
					)}
					<div className={styles.addButtonContainer}>
						<Button variant="secondary" small onClick={handleAddCustomEngine}>
							{addButtonLabel}
						</Button>
					</div>
				</SettingsTabSection>
			</>
		);
	},
);
export const SearchEnginesTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const query = '{query}';
	const url = '{url}';
	return (
		<>
			<EngineSection
				mode="text"
				store={SearchEngine}
				defaultTitle={t`Default Search Engine`}
				defaultDescription={t`Choose which search engine is used by default when searching selected text.`}
				builtInTitle={t`Built-in Search Engines`}
				builtInDescription={t`Enable or disable built-in search engines. Enabled engines appear in the message context menu when text is selected.`}
				customTitle={t`Custom Search Engines`}
				customDescription={
					<Trans>
						Add your own search engines with a custom URL pattern. Use <code>{query}</code> as a placeholder for the
						search text.
					</Trans>
				}
				addButtonLabel={t`Add Search Engine`}
				noEnginesText={t`Enable at least one search engine below.`}
				confirmTitle={t`Remove search engine`}
			/>
			<EngineSection
				mode="translate"
				store={Translation}
				defaultTitle={t`Default Translator`}
				defaultDescription={t`Choose which translator is used by default when translating selected text.`}
				builtInTitle={t`Built-in Translators`}
				builtInDescription={t`Enable or disable built-in translators. Enabled translators appear in the message context menu when text is selected.`}
				customTitle={t`Custom Translators`}
				customDescription={
					<Trans>
						Add your own translators with a custom URL pattern. Use <code>{query}</code> as a placeholder for the text
						to translate.
					</Trans>
				}
				addButtonLabel={t`Add Translator`}
				noEnginesText={t`Enable at least one translator below.`}
				confirmTitle={t`Remove translator`}
			/>
			<EngineSection
				mode="image"
				store={ReverseImageSearch}
				defaultTitle={t`Default Reverse Image Search`}
				defaultDescription={t`Choose which reverse image search service is used by default when searching an image.`}
				builtInTitle={t`Built-in Reverse Image Search`}
				builtInDescription={t`Enable or disable built-in reverse image search providers. Enabled providers appear in the context menu of images, avatars, banners, stickers, and emoji.`}
				customTitle={t`Custom Reverse Image Search`}
				customDescription={
					<Trans>
						Add your own reverse image search providers with a custom URL pattern. Use <code>{url}</code> as a
						placeholder for the image URL.
					</Trans>
				}
				addButtonLabel={t`Add Reverse Image Search`}
				noEnginesText={t`Enable at least one reverse image search provider below.`}
				confirmTitle={t`Remove reverse image search`}
			/>
		</>
	);
});
